import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiPhone } from 'react-icons/fi';
import styled from 'styled-components';
import colors from '../../styles/colors';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 15px 0;
  transition: all 0.3s ease;
  background: ${props => props.$scrolled ? colors.background.card : 'transparent'};
  box-shadow: ${props => props.$scrolled ? `0 2px 20px ${colors.shadowLight}` : 'none'};
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid transparent;
  background: linear-gradient(${colors.background.card}, ${colors.background.card}) padding-box,
    ${colors.metallic.silver} border-box;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
`;

const LogoText = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${colors.text.heading};

  span {
    color: ${colors.accent.main};
  }

  @media (max-width: 500px) {
    font-size: 1.2rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: 900px) {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: ${colors.background.card};
    flex-direction: column;
    padding: 30px;
    gap: 25px;
    box-shadow: 0 10px 30px ${colors.shadowLight};
    transform: ${props => props.$open ? 'translateY(0)' : 'translateY(-150%)'};
    opacity: ${props => props.$open ? 1 : 0};
    transition: all 0.3s ease;
  }
`;

const NavLink = styled(Link)`
  font-family: 'Oswald', sans-serif;
  font-weight: 500;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${props => props.$active ? colors.accent.main : colors.text.light};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: ${props => props.$active ? '100%' : '0'};
    height: 2px;
    background: ${colors.accent.main};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${colors.text.heading};
    &::after {
      width: 100%;
    }
  }
`;

const CTAButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  background: ${colors.accent.gradient};
  color: ${colors.text.white};
  font-family: 'Oswald', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 6px;
  border: 1px solid ${colors.accent.light};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 15px rgba(0, 102, 177, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 6px 25px rgba(0, 102, 177, 0.5);
  }
`;

const MobileButton = styled.button`
  display: none;
  color: ${colors.text.heading};
  background: none;
  border: none;
  cursor: pointer;

  @media (max-width: 900px) {
    display: block;
  }
`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const isActive = (path) => location.pathname === path;

  return (
    <HeaderContainer $scrolled={scrolled}>
      <Nav>
        <Logo to="/" onClick={closeMenu}>
          <LogoImage src="/images/logo.jpg" alt="The Honest Mechanic" />
          <LogoText>The Honest <span>Mechanic</span></LogoText>
        </Logo>

        <NavLinks $open={menuOpen}>
          <NavLink to="/services" $active={isActive('/services')} onClick={closeMenu}>Services</NavLink>
          <NavLink to="/academy" $active={isActive('/academy')} onClick={closeMenu}>HM Academy</NavLink>
          <NavLink to="/bmw" $active={isActive('/bmw')} onClick={closeMenu}>BMW Specialty</NavLink>
          <NavLink to="/about" $active={isActive('/about')} onClick={closeMenu}>About Us</NavLink>
          <NavLink to="/contact" $active={isActive('/contact')} onClick={closeMenu}>Contact</NavLink>
          <CTAButton to="/contact" onClick={closeMenu}>
            <FiPhone /> Call Us
          </CTAButton>
        </NavLinks>

        <MobileButton onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </MobileButton>
      </Nav>
    </HeaderContainer>
  );
}
